import type { WarehouseEntry } from '../types/warehouse';

export interface WarehouseStockRow {
  key: string;
  coffeeTypeId: string;
  coffeeTypeName: string;
  unit: string;
  totalImport: number;
  totalExportStore: number;
  totalExportPartner: number;
  totalExport: number;
  stock: number;
  hasNegativeStock: boolean;
}

const stockKey = (entry: Pick<WarehouseEntry, 'coffeeTypeId' | 'unit'>) =>
  `${String(entry.coffeeTypeId || '').trim()}__${String(entry.unit || 'g').trim().toLowerCase()}`;

export const buildWarehouseStockRows = (entries: WarehouseEntry[]): WarehouseStockRow[] => {
  const rows = new Map<string, WarehouseStockRow>();

  entries.forEach((entry) => {
    const key = stockKey(entry);
    const quantity = Number(entry.quantity || 0);
    const isExport = entry.entryType === 'export';
    const exportQuantity = isExport ? Math.abs(quantity) : 0;
    const row = rows.get(key) || {
      key,
      coffeeTypeId: entry.coffeeTypeId,
      coffeeTypeName: entry.coffeeTypeName,
      unit: String(entry.unit || 'g').trim(),
      totalImport: 0,
      totalExportStore: 0,
      totalExportPartner: 0,
      totalExport: 0,
      stock: 0,
      hasNegativeStock: false,
    };

    if (isExport) {
      if (entry.exportTargetType === 'partner' || Boolean(entry.partnerId)) {
        row.totalExportPartner += exportQuantity;
      } else {
        row.totalExportStore += exportQuantity;
      }
      row.totalExport += exportQuantity;
    } else {
      row.totalImport += quantity;
    }

    row.stock = row.totalImport - row.totalExport;
    row.hasNegativeStock = row.stock < 0;
    rows.set(key, row);
  });

  return Array.from(rows.values()).sort((a, b) =>
    a.coffeeTypeName.localeCompare(b.coffeeTypeName, 'vi'),
  );
};

export const validateWarehouseExport = (availableStock: number, requestedQuantity: number): number => {
  const quantity = Number(requestedQuantity);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    throw new Error('Số lượng xuất phải lớn hơn 0.');
  }

  const available = Number.isFinite(Number(availableStock)) ? Number(availableStock) : 0;
  if (quantity > available) {
    throw new Error(`Số lượng xuất vượt tồn khả dụng (${available}).`);
  }

  return quantity;
};

export const getWarehouseAvailableStock = (
  entries: WarehouseEntry[],
  coffeeTypeId: string,
  unit: string,
  excludedEntryId?: string,
): number => {
  const targetKey = `${String(coffeeTypeId || '').trim()}__${String(unit || 'g').trim().toLowerCase()}`;
  return buildWarehouseStockRows(entries.filter((entry) => entry.id !== excludedEntryId))
    .find((row) => row.key === targetKey)?.stock || 0;
};
